import { useNotificationAttention } from "./useNotificationAttention";
import React, { memo, useState, useCallback, useEffect } from "react";
import classNames from "classnames";
import styles from "./Header.module.scss";
import { useSelector } from "react-redux";
import { useAppDispatch } from "Redux/store";
import { getUnreadCount } from "Redux/Announcements/reducer";
import { selectUnreadSuccess } from "Redux/Announcements/slice";
import {
  selectNotificationsCount,
  selectUrgentNotificationsCount,
  selectPriorityNotificationsCount,
  setNotificationScope,
} from "Redux/Notifications/slice";
import { selectPermissions } from "Redux/Role/slice";
import { MODULES } from "Shared/Types/permissions";
import { readObj } from "Service/storage";
import { CommandLauncher } from "Components/CommandCenter/CommandCenterProvider";
import ModalUserProfile from "Components/ModalUserProfile/ModalUserProfile";
import { Notification } from "Components/Notification/Notification";
import { Profile } from "Components/UI/Profile/Profile";
import { NotificationIcon, ThemeIcon, BurgerIcon } from "Assets";
import SafeImage from "Components/UI/SafeImage/SafeImage";
import { toggleTheme } from "Shared/Hook/useTheme";

type HeaderProps = { onToggleMenu?: () => void; isMenuOpen?: boolean };

const Header: React.FC<HeaderProps> = ({ onToggleMenu, isMenuOpen }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean | null>(
    null,
  );

  const dispatch = useAppDispatch();
  const personalInfo = readObj("account");
  const accountScope = JSON.stringify([
    personalInfo?.id,
    personalInfo?.company,
  ]);
  useEffect(() => {
    dispatch(setNotificationScope(accountScope));
  }, [accountScope, dispatch]);
  const hasUrgentNotifications = useSelector(selectUrgentNotificationsCount);
  const notificationsCount = useSelector(selectNotificationsCount);
  const priorityCount = useSelector(selectPriorityNotificationsCount);
  const attention = useNotificationAttention(priorityCount);
  const isUnreadCountFirstSuccess = useSelector(selectUnreadSuccess);
  const permissions = useSelector(selectPermissions);
  const notificationClasses = classNames(styles.notification, {
    [styles.notification_normal]: notificationsCount > 0,
    [styles.notification_urgent]: hasUrgentNotifications,
    [styles.notification_attention]: attention,
  });

  const hasNotificationAccess =
    permissions?.has_full_access ||
    permissions?.modules.has(MODULES.NOTIFICATIONS);

  const handleOpenNotifications = useCallback(() => {
    setIsNotificationOpen((prev) => !prev);
  }, []);
  const handleCloseNotifications = useCallback(() => {
    setIsNotificationOpen(false);
  }, []);

  const handleProfileClick = useCallback(() => {
    setIsProfileOpen(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isUnreadCountFirstSuccess) {
      dispatch(getUnreadCount());
    }
  }, [dispatch, isUnreadCountFirstSuccess]);

  useEffect(() => {
    if (!isUnreadCountFirstSuccess) {
      dispatch(getUnreadCount());
    }
  }, [dispatch, isUnreadCountFirstSuccess]);

  return (
    <div className={styles.contant}>
      <div className={classNames(styles.contant__display, styles.headerRow)}>
        {onToggleMenu && (
          <button
            type="button"
            className={styles.menuButton}
            onClick={onToggleMenu}
            aria-label="Open navigation menu"
            aria-expanded={!!isMenuOpen}
            aria-controls="main-navigation"
          >
            <BurgerIcon aria-hidden="true" width={24} height={24} />
          </button>
        )}
        <div className={styles.globalSearch}>
          <CommandLauncher />
        </div>

        <div
          className={classNames(styles.contant__display, styles.headerActions)}
        >
          {hasNotificationAccess && (
            <div
              data-notification={
                notificationsCount > 99 ? "99+" : notificationsCount
              }
              className={notificationClasses}
            >
              <button
                type="button"
                className={styles.notificationButton}
                onClick={handleOpenNotifications}
                aria-label={`Notifications, ${notificationsCount} unread${hasUrgentNotifications ? `, ${priorityCount} high-priority unread` : ""}`}
                aria-expanded={!!isNotificationOpen}
                aria-controls="notifications-panel"
                aria-haspopup="dialog"
              >
                <NotificationIcon
                  aria-hidden="true"
                  className={classNames(
                    styles.contant__display_blockIcon_icon,
                    styles.contant__display_blockIcon_icon_notification,
                  )}
                />
              </button>
              <Notification
                isOpen={isNotificationOpen}
                onClose={handleCloseNotifications}
              />
            </div>
          )}

          <button
            type="button"
            aria-label="Toggle light and dark theme"
            onClick={toggleTheme}
            className={styles.theme}
          >
            <ThemeIcon aria-hidden="true" width="23px" height="23px" />
          </button>

          <div
            onClick={handleProfileClick}
            onMouseEnter={handleMouseEnter}
            className={styles.contant__display_blockIcon}
          >
            {/* A failed photo used to be hidden, leaving an invisible button. */}
            <SafeImage
              src={personalInfo?.profile_picture?.file}
              alt="User profile"
              className={styles.contant__display_blockIcon_icon}
              fallback={
                <Profile
                  className={classNames(
                    styles.contant__display_blockIcon_icon,
                    styles.contant__display_blockIcon_profileIcon,
                  )}
                />
              }
            />

            {isProfileOpen && <ModalUserProfile setOpen={setIsProfileOpen} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Header);
